import { useState } from 'react';
import OverseasGoogleMap from './OverseasGoogleMap';
import {
  OVERSEAS_COUNTRIES,
  OVERSEAS_COUNTRY_ETC,
  OVERSEAS_REGIONS,
  composeOverseasAddress,
  type OverseasLocationValue,
} from './overseasData';

type Props = {
  value: OverseasLocationValue;
  onChange: (next: OverseasLocationValue) => void;
};

/**
 * 해외사역지 공고 위치·주소 입력 — 대륙 → 국가 → 도시 → 현지 주소(주소1·2, 주/도, 우편번호)
 * 공고 등록(RecruitOverseasPost) · 관리자 수정 · 마이페이지 수정(PostingEdit) 공용
 */
export default function OverseasLocationFields({ value, onChange }: Props) {
  const countryOptions = value.region ? OVERSEAS_COUNTRIES[value.region] ?? [] : [];
  // 목록에 없는 국가가 저장돼 있으면 직접 입력 모드로 보여준다
  const [customCountry, setCustomCountry] = useState(
    Boolean(value.country) && !countryOptions.includes(value.country),
  );
  const [previewAddress, setPreviewAddress] = useState('');

  const update = (patch: Partial<OverseasLocationValue>) => onChange({ ...value, ...patch });

  const selectRegion = (region: string) => {
    if (region === value.region) {
      update({ region: '', country: '' });
    } else {
      update({ region, country: '' });
    }
    setCustomCountry(false);
  };

  const selectCountry = (selected: string) => {
    if (selected === OVERSEAS_COUNTRY_ETC) {
      setCustomCountry(true);
      update({ country: '' });
      return;
    }
    setCustomCountry(false);
    update({ country: selected });
  };

  const fullAddress = composeOverseasAddress(value);

  return (
    <>
      <div className="inputBox">
        <h3 className='title'><p>지역(대륙)</p></h3>
        <div className="inputRow">
          <div className='checkTextRow'>
            {OVERSEAS_REGIONS.map((item) => (
              <div key={item} className='checkTextBox' onClick={() => selectRegion(item)}>
                <input className="checkbox" type="checkbox" checked={value.region === item} readOnly />
                <span className="checkmark" />
                <div className='imgtextBox'>
                  <p className={value.region === item ? 'selectedLocation' : ''}>{item}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="inputBox">
        <h3 className='title'><p>국가</p></h3>
        <div className="inputRow">
          {!value.region ? (
            <p style={{ fontSize: '16px', color: 'var(--ink-4)' }}>지역(대륙)을 먼저 선택해주세요</p>
          ) : (
            <div className="overseas-location-row">
              <select
                className="dropdownBox overseas-location-select"
                value={customCountry ? OVERSEAS_COUNTRY_ETC : value.country}
                onChange={(e) => selectCountry(e.target.value)}
              >
                <option value="">국가 선택</option>
                {countryOptions.map((country) => (
                  <option key={country} value={country}>{country}</option>
                ))}
                <option value={OVERSEAS_COUNTRY_ETC}>{OVERSEAS_COUNTRY_ETC}</option>
              </select>
              {customCountry && (
                <input
                  value={value.country}
                  className="inputdefault"
                  type="text"
                  placeholder="국가명을 입력해주세요"
                  onChange={(e) => update({ country: e.target.value })}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="inputBox">
        <h3 className='title'><p>도시</p></h3>
        <div className="inputRow">
          <input
            value={value.city}
            className="inputdefault"
            type="text"
            placeholder="예) 로스앤젤레스 / Los Angeles"
            onChange={(e) => update({ city: e.target.value })}
          />
        </div>
      </div>

      <div className="inputBox">
        <h3 className='title'><p>주소</p></h3>
        <div className="inputRow recruit-address-fields">
          <input
            value={value.addressLine1}
            className="inputdefault"
            type="text"
            placeholder="주소 1 (도로명·번지) 예) 1234 Wilshire Blvd"
            onChange={(e) => update({ addressLine1: e.target.value })}
          />
          <input
            value={value.addressLine2}
            className="inputdefault"
            type="text"
            placeholder="주소 2 (건물명·호수, 선택) 예) Suite 200"
            onChange={(e) => update({ addressLine2: e.target.value })}
          />
          <div className="overseas-location-row">
            <input
              value={value.state}
              className="inputdefault"
              type="text"
              placeholder="주/도 (선택) 예) CA"
              onChange={(e) => update({ state: e.target.value })}
            />
            <input
              value={value.postalCode}
              className="inputdefault"
              type="text"
              placeholder="우편번호 (선택)"
              onChange={(e) => update({ postalCode: e.target.value })}
            />
          </div>
          <p className="recruit-address-guide">
            현지 표기(영문 등)로 입력하면 구글 지도에서 더 정확하게 찾을 수 있습니다.
          </p>
          <div className="overseas-location-preview">
            <span className="overseas-location-preview__text">{fullAddress || '입력한 주소가 여기에 표시됩니다.'}</span>
            <button
              type="button"
              className="recruit-address-search-btn"
              disabled={!fullAddress}
              onClick={() => setPreviewAddress(fullAddress)}
            >
              지도에서 확인
            </button>
          </div>
          {previewAddress && (
            <div className="overseas-location-map">
              <OverseasGoogleMap address={previewAddress} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
